import IframeView from 'epubjs/src/managers/views/iframe';
import { EVENTS } from 'epubjs/src/utils/constants';
import VIEW_FLIPPING_STATE from './viewflippingstate';
import { getDiffBetweenIframeAndBodyWidth, getViewBodyElement } from '../../../iframe-utils';

class FlippingIframeView extends IframeView {
  constructor(section, options) {
    super(section, options);

    this.viewFlippingState = this.settings.viewFlippingState;

    this.setFlippingState(this.viewFlippingState);
  }

  /**
   * If the page contents is already aligned to left
   * @returns {boolean}
   */
  isAlreadyAlignedToLeftAndContentDoesNOTNeedToBePushed() {
    return (
      this.viewFlippingState === VIEW_FLIPPING_STATE.READABLE_PAGE_RIGHT ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.RIGHT_PAGE_FLIPPING_TO_LEFT ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_RIGHT_SIDE ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_RIGHT_SIDE_FLIPPING_LEFT ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_LEFT_ON_RIGHT_SIDE ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_LEFT_ON_RIGHT_SIDE_FLIPPING_RIGHT
    );
  }

  /**
   * If the page itself is displayed on the right side of the player
   */
  isOnRightSideOfThePlayer() {
    return (
      this.viewFlippingState === VIEW_FLIPPING_STATE.READABLE_PAGE_RIGHT ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.RIGHT_PAGE_FLIPPING_TO_LEFT ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_RIGHT_SIDE ||
      this.viewFlippingState === VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_RIGHT_SIDE_FLIPPING_LEFT
    );
  }

  /**
   * This is mostly as the parent implementation, but adding the inline styles update when changing state.
   */
  render(request, _show) {
    // view.onLayout = this.layout.format.bind(this.layout);
    this.create();

    // Fit to size of the container, apply padding
    this.size();

    if (!this.sectionRender) {
      this.sectionRender = this.section.render(request);
    }

    // Render Chain
    return this.sectionRender
      .then((contents) => this.load(contents))
      .then(
        () => {
          // find and report the writingMode axis
          const writingMode = this.contents.writingMode();

          // Set the axis based on the flow and writing mode
          let axis;
          if (this.settings.flow === 'scrolled') {
            axis = writingMode.indexOf('vertical') === 0 ? 'horizontal' : 'vertical';
          } else {
            axis = writingMode.indexOf('vertical') === 0 ? 'vertical' : 'horizontal';
          }

          if (writingMode.indexOf('vertical') === 0 && this.settings.flow === 'paginated') {
            this.layout.delta = this.layout.height;
          }

          this.setAxis(axis);
          this.emit(EVENTS.VIEWS.AXIS, axis);

          this.setWritingMode(writingMode);
          this.emit(EVENTS.VIEWS.WRITING_MODE, writingMode);

          // apply the layout function to the contents
          this.layout.format(this.contents, this.section, this.axis);

          // Listen for events that require an expansion of the iframe
          this.addListeners();

          return new Promise((resolve, _reject) => {
            // Expand the iframe to the full size of the content
            this.expand();

            this.updateInlineStyle();

            resolve();
          });
        },
        (e) => {
          this.emit(EVENTS.VIEWS.LOAD_ERROR, e);
          return new Promise((resolve, reject) => {
            reject(e);
          });
        },
      )
      .then(() => {
        this.emit(EVENTS.VIEWS.RENDERED, this.section);
      });
  }

  setFlippingState(newFlippingState) {
    // The CSS classes are not used for CSS anymore, but they are useful for debugging when tracking the behavior.
    this.element.classList.remove(this.viewFlippingState);

    this.viewFlippingState = newFlippingState;
    this.element.classList.add(this.viewFlippingState);

    this.updateInlineStyle();

    if (
      [
        VIEW_FLIPPING_STATE.FLIPPABLE_FROM_LEFT_ON_LEFT_SIDE,
        VIEW_FLIPPING_STATE.FLIPPABLE_FROM_LEFT_ON_RIGHT_SIDE,
        VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_LEFT_SIDE,
        VIEW_FLIPPING_STATE.FLIPPABLE_FROM_RIGHT_ON_RIGHT_SIDE,
      ].includes(newFlippingState)
    ) {
      // Some inconsistent missing check in super.hide()
      if (this.iframe) {
        this.hide();
      }
    }
  }

  getBodyElement() {
    return getViewBodyElement(this);
  }

  getBodyLeftOffset() {
    if (this.isAlreadyAlignedToLeftAndContentDoesNOTNeedToBePushed()) {
      return 0;
    }

    return getDiffBetweenIframeAndBodyWidth(this);
  }

  updateInlineStyle() {
    this.element.style.position = 'absolute';

    // To know that we are referring the coordinates to the top left corner. Also necessary for RTL books.
    this.element.style.left = '0';

    let leftOffset = 0;
    if (this.isOnRightSideOfThePlayer()) {
      leftOffset = this.width();
    }
    /*
    We use translate3d(x, 0, 0) instead of left because it will be overriden by the transform during the animation.
    If we would have used style.left, we end up with two properties that need to be updated, because they add up.
    * */
    this.element.style.transform = `translate3d(${leftOffset}px, 0, 0)`;

    /* When on the left side, the actual content page might be more narrow than the iframe,
        so we need to bring it to the "middle of the book".

        Same thing that we did in the documentFix.ts, but doing it here
         for having control of how the pages are animated and positioned.
         */
    const bodyLeftOffset = this.getBodyLeftOffset();
    const bodyElement = this.getBodyElement();
    if (bodyElement) {
      bodyElement.style.marginLeft = `${bodyLeftOffset}px`;
    }
  }

  isShown() {
    return this.element.style.visibility === 'visible';
  }
}

export default FlippingIframeView;
